import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'

import { relayablyApi } from './relayablyApi'
import { sessionStorage } from './storage'
import { AuthFormValues, AuthProvider, AuthSession, UserProfile } from './types'

type AuthState = {
  status: 'idle' | 'loading' | 'authenticated' | 'error'
  hydrated: boolean
  sessionToken: string | null
  user: UserProfile | null
  errorMessage: string | null
}

const initialState: AuthState = {
  status: 'idle',
  hydrated: false,
  sessionToken: null,
  user: null,
  errorMessage: null,
}

const persistSession = async (session: AuthSession | null) => {
  if (!session) {
    await sessionStorage.clear()
    return
  }

  await sessionStorage.set(JSON.stringify(session))
}

export const bootstrapSession = createAsyncThunk('auth/bootstrapSession', async () => {
  const rawSession = await sessionStorage.get()

  if (!rawSession) {
    return null
  }

  try {
    const parsed = JSON.parse(rawSession) as AuthSession
    const session = await relayablyApi.getMe(parsed.sessionToken)
    await persistSession(session)
    return session
  } catch (error) {
    const message = error instanceof Error ? error.message : ''
    if (message.includes('This session has expired')) {
      await persistSession(null)
      return null
    }

    throw error
  }
})

export const submitAuth = createAsyncThunk('auth/submitAuth', async (formValues: AuthFormValues) => {
  const session = await relayablyApi.submitAuth({
    mode: formValues.mode,
    method: formValues.method,
    email: formValues.email,
    password: formValues.password,
    fullName: formValues.fullName,
    phoneNumber: formValues.phoneNumber,
    otp: formValues.otp,
  })

  await persistSession(session)
  return session
})

export const logout = createAsyncThunk('auth/logout', async (sessionToken: string | null) => {
  if (sessionToken) {
    await relayablyApi.logout(sessionToken)
  }

  await persistSession(null)
  return null
})

export const linkProvider = createAsyncThunk(
  'auth/linkProvider',
  async ({ sessionToken, provider }: { sessionToken: string; provider: AuthProvider }) => {
    const session = await relayablyApi.linkProvider(sessionToken, provider)
    await persistSession(session)
    return session
  }
)

export const signInWithAppleSession = createAsyncThunk('auth/signInWithAppleSession', async () => {
  const session = await relayablyApi.signInWithApple()
  await persistSession(session)
  return session
})

export const signInWithGoogleSession = createAsyncThunk('auth/signInWithGoogleSession', async () => {
  const session = await relayablyApi.signInWithGoogle()
  await persistSession(session)
  return session
})

export const signInWithLinkedInRedirect = createAsyncThunk('auth/signInWithLinkedInRedirect', async () => {
  await relayablyApi.signInWithLinkedIn()
  return null
})

export const completeLinkedInSession = createAsyncThunk('auth/completeLinkedInSession', async () => {
  const session = await relayablyApi.completeLinkedInSignIn()
  await persistSession(session)
  return session
})

export const unlinkProvider = createAsyncThunk(
  'auth/unlinkProvider',
  async ({ sessionToken, provider }: { sessionToken: string; provider: AuthProvider }) => {
    const session = await relayablyApi.unlinkProvider(sessionToken, provider)
    await persistSession(session)
    return session
  }
)

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    dismissError(state) {
      state.errorMessage = null
    },
    applySession(state, action: PayloadAction<AuthSession | null>) {
      state.sessionToken = action.payload?.sessionToken ?? null
      state.user = action.payload?.user ?? null
      state.status = action.payload ? 'authenticated' : 'idle'
      state.errorMessage = null
      state.hydrated = true
    },
  },
  extraReducers: builder => {
    builder
      .addCase(bootstrapSession.pending, state => {
        state.status = 'loading'
      })
      .addCase(bootstrapSession.fulfilled, (state, action) => {
        state.sessionToken = action.payload?.sessionToken ?? null
        state.user = action.payload?.user ?? null
        state.status = action.payload ? 'authenticated' : 'idle'
        state.hydrated = true
        state.errorMessage = null
      })
      .addCase(bootstrapSession.rejected, (state, action) => {
        state.status = 'idle'
        state.hydrated = true
        state.sessionToken = null
        state.user = null
        state.errorMessage = action.error.message ?? 'Unable to restore your session.'
      })
      .addCase(submitAuth.pending, state => {
        state.status = 'loading'
        state.errorMessage = null
      })
      .addCase(submitAuth.fulfilled, (state, action) => {
        state.status = 'authenticated'
        state.sessionToken = action.payload.sessionToken
        state.user = action.payload.user
      })
      .addCase(submitAuth.rejected, (state, action) => {
        state.status = 'error'
        state.errorMessage = action.error.message ?? 'Unable to sign you in.'
      })
      .addCase(logout.fulfilled, state => {
        state.status = 'idle'
        state.sessionToken = null
        state.user = null
      })
      .addCase(linkProvider.fulfilled, (state, action) => {
        state.status = 'authenticated'
        state.sessionToken = action.payload.sessionToken
        state.user = action.payload.user
      })
      .addCase(signInWithAppleSession.pending, state => {
        state.status = 'loading'
        state.errorMessage = null
      })
      .addCase(signInWithAppleSession.fulfilled, (state, action) => {
        state.status = 'authenticated'
        state.sessionToken = action.payload.sessionToken
        state.user = action.payload.user
      })
      .addCase(signInWithAppleSession.rejected, (state, action) => {
        state.status = 'error'
        state.errorMessage = action.error.message ?? 'Unable to sign in with Apple.'
      })
      .addCase(signInWithGoogleSession.pending, state => {
        state.status = 'loading'
        state.errorMessage = null
      })
      .addCase(signInWithGoogleSession.fulfilled, (state, action) => {
        state.status = 'authenticated'
        state.sessionToken = action.payload.sessionToken
        state.user = action.payload.user
      })
      .addCase(signInWithGoogleSession.rejected, (state, action) => {
        state.status = 'error'
        state.errorMessage = action.error.message ?? 'Unable to sign in with Google.'
      })
      .addCase(signInWithLinkedInRedirect.pending, state => {
        state.status = 'loading'
        state.errorMessage = null
      })
      .addCase(signInWithLinkedInRedirect.rejected, (state, action) => {
        state.status = 'error'
        state.errorMessage = action.error.message ?? 'Unable to start LinkedIn sign-in.'
      })
      .addCase(completeLinkedInSession.pending, state => {
        state.status = 'loading'
        state.errorMessage = null
      })
      .addCase(completeLinkedInSession.fulfilled, (state, action) => {
        state.status = 'authenticated'
        state.sessionToken = action.payload.sessionToken
        state.user = action.payload.user
      })
      .addCase(completeLinkedInSession.rejected, (state, action) => {
        state.status = 'error'
        state.errorMessage = action.error.message ?? 'Unable to complete LinkedIn sign-in.'
      })
      .addCase(unlinkProvider.fulfilled, (state, action) => {
        state.status = 'authenticated'
        state.sessionToken = action.payload.sessionToken
        state.user = action.payload.user
      })
      .addMatcher(
        action =>
          action.type === linkProvider.rejected.type ||
          action.type === unlinkProvider.rejected.type ||
          action.type === logout.rejected.type,
        (state, action: { error?: { message?: string } }) => {
          state.errorMessage = action.error?.message ?? 'That action could not be completed.'
        }
      )
  },
})

export const { applySession, dismissError } = authSlice.actions

export const authReducer = authSlice.reducer
